import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { MultiColumnCard } from "components/Card/MultiColumnCard.tsx";
import { KaboodleToaster } from "components/common";
import KaboodleModal from "components/Modals/KaboodleModal";
import SurgicalPricingModal from "components/SurgicalPricingModal/SurgicalPricingModal";
import { Tooltip } from "components/Tooltip/Tooltip";
import { useCallback, useMemo, useState } from "react";
import {
  useCreateSurgicalPricingCalculationMutation,
  useUpdateSurgicalPricingCalculationMutation,
} from "services/future-medical-api";
import {
  useCloneTreatmentAllocationMutation,
  useCreateTreatmentAllocationMutation,
  useDeleteTreatmentAllocationByIdMutation,
  useMoveTreatmentAllocationMutation,
} from "services/msa-api";
import type {
  MSADataType,
  SurgicalPricingCalculationType,
} from "types/DataTypes";

import { getServiceField } from "../../future-medical-utils";
import { TreatmentAllocationRow } from "../components/AllocationRow/TreatmentAllocationRow";
import { DroppableZone } from "../components/DroppableZone/DroppableZone";
import {
  ALLOCATION_DND_TYPES,
  TREATMENT_ALLOCATION_DEFAULT_NAME,
} from "../utils/allocation-constants";
import { calculateDropPosition } from "../utils/allocation-drop-utils";

import type {
  AllocationService,
  TreatmentAllocationRowProps,
} from "../utils/allocation-types";
import type { SurgicalPricingFormData } from "components/SurgicalPricingModal/SurgicalPricingModal";
import type { AllocationFilterValue } from "../components/AllocationFilter/AllocationFilter";

type Props = {
  isLoadingAllRows: boolean;
  service: AllocationService;
  allRows: TreatmentAllocationRowProps[];
  allocationFilter?: AllocationFilterValue;
  reviewType: "MEDICAL" | "LEGAL";
};

/**
 * card for treatment allocations
 * displays included and excluded treatment allocations
 * supports surgical pricing calculations
 * handles row deletion and drag-drop operations
 */
export const TreatmentAllocationCard = ({
  isLoadingAllRows,
  service,
  allRows,
  allocationFilter = "ALL",
  reviewType,
}: Props) => {
  const [rowToDelete, setRowToDelete] =
    useState<TreatmentAllocationRowProps | null>(null);
  const [showSurgicalPricing, setShowSurgicalPricing] = useState(false);
  const [surgicalPricingTargetIsExcluded, setSurgicalPricingTargetIsExcluded] =
    useState(false);
  const [selectedSurgicalPricingId, setSelectedSurgicalPricingId] = useState<
    number | null
  >(null);

  const [createTreatmentAllocation, { isLoading: isCreating }] =
    useCreateTreatmentAllocationMutation();
  const [cloneTreatmentAllocation, { isLoading: isCloning }] =
    useCloneTreatmentAllocationMutation();
  const [deleteTreatmentAllocation, { isLoading: isDeleting }] =
    useDeleteTreatmentAllocationByIdMutation();
  const [moveTreatmentAllocation, { isLoading: isMoving }] =
    useMoveTreatmentAllocationMutation();
  const [createSurgicalPricingCalculation, { isLoading: isCreatingSurgical }] =
    useCreateSurgicalPricingCalculationMutation();
  const [updateSurgicalPricingCalculation, { isLoading: isUpdatingSurgical }] =
    useUpdateSurgicalPricingCalculationMutation();

  const isLoading =
    isCreating ||
    isDeleting ||
    isLoadingAllRows ||
    isCreatingSurgical ||
    isUpdatingSurgical ||
    isMoving ||
    isCloning;

  // filter rows based on medicare status
  const filteredRows = useMemo(() => {
    if (allocationFilter === "ALL") {
      return allRows;
    }
    if (allocationFilter === "MEDICARE") {
      return allRows.filter((row) => !row.non_medicare);
    }
    if (allocationFilter === "NON_MEDICARE") {
      return allRows.filter((row) => row.non_medicare);
    }
    return allRows;
  }, [allRows, allocationFilter]);

  const includedRowsBase = filteredRows
    .filter((row: TreatmentAllocationRowProps) => !row.is_excluded)
    .sort(
      (a: TreatmentAllocationRowProps, b: TreatmentAllocationRowProps) =>
        a.position - b.position,
    );

  const excludedRowsBase = filteredRows
    .filter((row: TreatmentAllocationRowProps) => row.is_excluded)
    .sort(
      (a: TreatmentAllocationRowProps, b: TreatmentAllocationRowProps) =>
        a.position - b.position,
    );

  // add section metadata to rows for drop indicator logic
  const includedRows = includedRowsBase.map((row) => ({
    ...row,
    totalRowsInSection: includedRowsBase.length,
  }));

  const excludedRows = excludedRowsBase.map((row) => ({
    ...row,
    totalRowsInSection: excludedRowsBase.length,
  }));

  const handleNewRow = useCallback(
    async (is_excluded: boolean) => {
      try {
        const data = {
          name: TREATMENT_ALLOCATION_DEFAULT_NAME,
          is_excluded,
          is_included_exposure_or_mitigation: true,
          ...getServiceField(service, false, reviewType),
        };

        await createTreatmentAllocation({
          data,
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to create new row");
      }
    },
    [createTreatmentAllocation, service, reviewType],
  );

  const handleDeleteRow = useCallback(
    async (row: TreatmentAllocationRowProps) => {
      try {
        const data = {
          ...getServiceField(service, false, reviewType),
        };

        await deleteTreatmentAllocation({
          id: row.id,
          data: {
            ...data,
            msa: (service as unknown as MSADataType).id.toString(),
          },
        }).unwrap();
        setRowToDelete(null);
      } catch (error) {
        KaboodleToaster.error(error);
      }
    },
    [deleteTreatmentAllocation, service, reviewType],
  );

  const handleCloneRow = useCallback(
    async (row: TreatmentAllocationRowProps) => {
      try {
        await cloneTreatmentAllocation({
          id: row.id,
        }).unwrap();
        KaboodleToaster.success("Row cloned successfully");
      } catch (error) {
        KaboodleToaster.error("Failed to clone row");
        console.error("Failed to clone row:", error);
      }
    },
    [cloneTreatmentAllocation],
  );

  const handleRowDrop = useCallback(
    async (
      draggedRow: TreatmentAllocationRowProps,
      dropTargetRow: TreatmentAllocationRowProps & {
        totalRowsInSection: number;
      },
    ) => {
      // don't do anything if dropping on itself
      if (draggedRow.id === dropTargetRow.id) return;

      const { targetPosition } = calculateDropPosition({
        draggedRow,
        dropTargetRow,
      });

      try {
        await moveTreatmentAllocation({
          id: draggedRow.id,
          data: {
            to_position: targetPosition,
            is_excluded: dropTargetRow.is_excluded,
          },
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to move treatment");
      }
    },
    [moveTreatmentAllocation],
  );

  const handleZoneDrop = useCallback(
    async (row: TreatmentAllocationRowProps, newIsExcluded: boolean) => {
      if (row.is_excluded === newIsExcluded) return;

      // calculate target position - append to end of target section
      const targetRows = allRows.filter((r) => r.is_excluded === newIsExcluded);
      const toPosition = targetRows.length;

      try {
        await moveTreatmentAllocation({
          id: row.id,
          data: {
            to_position: toPosition,
            is_excluded: newIsExcluded,
          },
        }).unwrap();
      } catch (error) {
        KaboodleToaster.error("Failed to move treatment");
      }
    },
    [allRows, moveTreatmentAllocation],
  );

  const handleShowSurgicalPricing = (id: number) => {
    setSelectedSurgicalPricingId(id);
    setSurgicalPricingTargetIsExcluded(false);
    setShowSurgicalPricing(true);
  };

  const handleSurgicalPricingSubmit = async (data: SurgicalPricingFormData) => {
    try {
      const apiData: Partial<SurgicalPricingCalculationType> = {
        surgery_name: data.surgery_name,
        state: data.state,
        zip_code: data.zip_code,
        procedure_type: data.procedure_type,
        anesthesia_code: data.anesthesia_code,
        anesthesia_description: data.anesthesia_description,
        base_unit: data.base_unit,
        total_units: data.total_units,
        conversion_factor: data.conversion_factor,
        cpt_codes: data.cpt_codes,
        adj_apc_payment: data.adj_apc_payment ? 1 : 0,
        facility_fee_apc_cpt: data.facility_fee_apc_cpt,
        mmc: data.mmc,
        ms_drg_code: data.ms_drg_code,
        ms_drg_description: data.ms_drg_description,
        ms_drg_base_price: data.ms_drg_base_price,
        state_multiplier: data.state_multiplier,
        organization: data.organization,
        ...getServiceField(service, true, reviewType),
      };

      if (selectedSurgicalPricingId) {
        await updateSurgicalPricingCalculation({
          id: selectedSurgicalPricingId,
          data: apiData,
        }).unwrap();
        KaboodleToaster.success("Surgical pricing updated");
      } else {
        await createSurgicalPricingCalculation({
          data: {
            ...apiData,
            is_excluded: surgicalPricingTargetIsExcluded,
          },
        }).unwrap();
        KaboodleToaster.success("Surgical pricing created");
      }
      setShowSurgicalPricing(false);
      setSurgicalPricingTargetIsExcluded(false);
    } catch (error) {
      KaboodleToaster.error(
        `Failed to ${selectedSurgicalPricingId ? "update" : "create"} surgical pricing`,
      );
    }
  };

  const isLumpSum = service.payout_method === "LUMP_SUM";

  const handleNewSurgicalPricing = (isExcluded: boolean) => {
    setSelectedSurgicalPricingId(null);
    setSurgicalPricingTargetIsExcluded(isExcluded);
    setShowSurgicalPricing(true);
  };

  const RowComponent = useCallback(
    (row: TreatmentAllocationRowProps & { totalRowsInSection: number }) => (
      <TreatmentAllocationRow
        {...row}
        showSeedCheckbox={!isLumpSum}
        service={service}
        onSetRowToDelete={setRowToDelete}
        onShowSurgicalPricing={handleShowSurgicalPricing}
        onDrop={handleRowDrop}
        onCloneRow={handleCloneRow}
      />
    ),
    // crucial to keep re-rendering to minimum for keepDirtyValues to work
    [isLumpSum, handleRowDrop, handleCloneRow],
  );

  const deleteModal = useMemo(() => {
    return (
      <>
        {rowToDelete && (
          <KaboodleModal
            isOpen={!!rowToDelete}
            onClose={() => setRowToDelete(null)}
            footer={
              <div className="flex jc-c">
                <Button
                  className="delete"
                  onClick={() => handleDeleteRow(rowToDelete)}
                >
                  Confirm Delete
                </Button>
                <Button onClick={() => setRowToDelete(null)}>Cancel</Button>
              </div>
            }
          >
            <Card heading="Delete Row">
              <p>Are you sure you want to delete this row?</p>
            </Card>
          </KaboodleModal>
        )}
      </>
    );
  }, [rowToDelete, handleDeleteRow]);

  return (
    <>
      {deleteModal}
      <SurgicalPricingModal
        isOpen={showSurgicalPricing}
        onClose={() => {
          setShowSurgicalPricing(false);
          setSelectedSurgicalPricingId(null);
          setSurgicalPricingTargetIsExcluded(false);
        }}
        onSubmit={handleSurgicalPricingSubmit}
        organization={service.organization}
        surgicalPricingId={selectedSurgicalPricingId}
        service={service}
      />
      <DroppableZone<TreatmentAllocationRowProps>
        dndType={ALLOCATION_DND_TYPES.TREATMENT}
        onDrop={(item) => handleZoneDrop(item, false)}
      >
        <MultiColumnCard
          classNames={`allocation-medical included ${!isLumpSum ? "seed" : ""}`}
          onNewRow={() => handleNewRow(false)}
          heading="Included Treatment Allocations"
          showSurgicalPricingButton={true}
          onSurgicalPricingClick={() => handleNewSurgicalPricing(false)}
          columnNames={[
            "",
            <Tooltip key="exposure" content="Exposure">
              E
            </Tooltip>,
            <Tooltip key="mitigation" content="Mitigation">
              M
            </Tooltip>,
            ...(isLumpSum
              ? []
              : [
                  <Tooltip key="seed" content="Seed">
                    S
                  </Tooltip>,
                ]),
            "Treatment",
            "CPT Code (s)",
            "Cost / Unit",
            "Units / Year",
            "Annual Cost",
            "# of Years",
            "Total",
            "",
            "",
          ]}
          initialRows={includedRows}
          RowComponent={RowComponent}
          getRowKey={(row) => row.id}
          isLoading={isLoading}
        />
      </DroppableZone>

      <DroppableZone<TreatmentAllocationRowProps>
        dndType={ALLOCATION_DND_TYPES.TREATMENT}
        onDrop={(item) => handleZoneDrop(item, true)}
      >
        <MultiColumnCard<TreatmentAllocationRowProps>
          classNames={`allocation-medical`}
          onNewRow={() => handleNewRow(true)}
          heading="Excluded Treatment Allocations"
          showSurgicalPricingButton={true}
          onSurgicalPricingClick={() => handleNewSurgicalPricing(true)}
          columnNames={[
            "",
            <Tooltip key="exposure" content="Exposure">
              E
            </Tooltip>,
            <Tooltip key="mitigation" content="Mitigation">
              M
            </Tooltip>,
            <Tooltip key="non-medicare" content="Non/Medicare">
              N/M
            </Tooltip>,
            "Treatment",
            "CPT Code",
            "Cost / Unit",
            "Units / Year",
            "Annual Cost",
            "# of Years",
            "Total",
            "",
            "",
          ]}
          initialRows={excludedRows}
          RowComponent={RowComponent}
          getRowKey={(row) => row.id}
          isLoading={isLoading}
        />
      </DroppableZone>
    </>
  );
};

export default TreatmentAllocationCard;
